/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import './ShipmentStatusTable.scss';
import { mainColors } from '../../helpers/colors';
import { useTranslation } from '../../hooks/useTranslation';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import {
  CCard,
  CCardBody,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CPagination,
  CPaginationItem,
  CBadge,
  CTooltip,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilArrowTop, cilArrowBottom, cilPencil, cilTrash } from '@coreui/icons';
import {
  updateShipmentStatusRequestAction,
  deleteShipmentStatusRequestAction,
} from '../../store/shipmentStatus/shipmentStatusActions';

const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] === null || b[orderBy] === undefined) {
    return -1;
  }
  if (a[orderBy] === null || a[orderBy] === undefined) {
    return 1;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

function ShipmentStatusTable(props) {
  const { 
    shipmentStatusList,
    shipmentStatusData,
    changeSortType,
    changeSortKey,
    shipmentHistorySortData,
    updateShipmentStatusRequest,
    deleteShipmentStatusRequest
  } = props;
  
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getHeaderProps, getResizerFor } = useResizableColumns(['index','ShipmentStatusName','ShipmentStatusDescription','actions'], 'ShipmentStatusTable');
  
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [deleteModal, setDeleteModal] = useState({ open: false, item: null });

  const columns = [
    { id: 'index', label: '#', sortable: false },
    { id: 'ShipmentStatusName', label: t('Status Name'), sortable: true },
    { id: 'ShipmentStatusDescription', label: t('Description'), sortable: true },
    { id: 'actions', label: t('Actions'), sortable: false }
  ];

  useEffect(() => {
    if (shipmentStatusList && Array.isArray(shipmentStatusList)) {
      setTotalRows(shipmentStatusList.length);
      setTableData(shipmentStatusList);
    }
  }, [shipmentStatusList]);

  const handleEdit = (item) => {
    console.log('Editing shipment status:', item);
    navigate(`/shipment-status/edit/${item.ShipmentStatusId}`, {
      state: { shipmentStatus: item }
    });
  };

  const handleDeleteClick = (item) => {
    setDeleteModal({ open: true, item });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.item) {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const sessionUserId = localStorage.getItem('sessionUserId');
      const userId = sessionUserId || userData?.sessionUserId;

      if (userId && userId !== 'undefined') {
        const deleteData = {
          SessionUserId: String(userId),
          ShipmentStatusId: deleteModal.item.ShipmentStatusId
        };

        console.log('Deleting shipment status:', deleteData);
        deleteShipmentStatusRequest({ data: deleteData });
      } else {
        console.error('User session not found');
      }
    }
    setDeleteModal({ open: false, item: null });
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ open: false, item: null });
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
    scrollToTableContainer();
  };

  const handleChangeRowsPerPage = (value) => {
    setRowsPerPage(value);
    setPage(1);
    scrollToTableContainer();
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    setOrder(newOrder);
    setOrderBy(property);

    if (changeSortType) changeSortType(newOrder);
    if (changeSortKey) changeSortKey(property);
  };

  function scrollToTableContainer() {
    const tableContainer = document.querySelector('.shipment-table-container');
    if (tableContainer) {
      tableContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const getSortedData = () => {
    const stabilizedData = stableSort(tableData, getComparator(order, orderBy));
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return stabilizedData.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalRows / rowsPerPage);
  
  const paginationItems = [];
  for (let i = 1; i <= totalPages; i++) {
    paginationItems.push(
      <CPaginationItem 
        key={i} 
        active={i === page} 
        onClick={() => handleChangePage(i)}
      >
        {i}
      </CPaginationItem>
    );
  }
  
  return (
    <div className="shipment-status-table-container">
      <CCard className="mb-4">
        <CCardBody>
          <CTable hover responsive>
            <CTableHead>
              <CTableRow>
                {columns.map((column) => {
                  const headerProps = getHeaderProps(column.id);
                  return (
                    <CTableHeaderCell 
                      key={column.id}
                      className={`table-header-cell ${column.sortable ? 'sortable-header' : ''}`}
                      onClick={() => column.sortable && handleRequestSort(column.id)}
                      style={{ cursor: column.sortable ? 'pointer' : 'default', paddingRight: '8px', ...(headerProps.style || {}) }}
                    >
                      {column.label}
                      {column.sortable && orderBy === column.id && (
                        <CIcon
                          icon={order === 'asc' ? cilArrowTop : cilArrowBottom}
                          size="sm"
                          className="ms-1"
                        />
                      )}
                      {getResizerFor(column.id)}
                    </CTableHeaderCell>
                  );
                })}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {getSortedData().length > 0 ? (
                getSortedData().map((item, index) => (
                  <CTableRow key={item.ShipmentStatusId || index}>
                    <CTableDataCell>
                      {(page - 1) * rowsPerPage + index + 1}
                    </CTableDataCell>
                    <CTableDataCell>
                      <CBadge color="primary" shape="rounded-pill">
                        {item.ShipmentStatusName}
                      </CBadge>
                    </CTableDataCell>
                    <CTableDataCell>
                      <span title={item.ShipmentStatusDescription}>
                        {item.ShipmentStatusDescription?.length > 50 
                          ? `${item.ShipmentStatusDescription.substring(0, 50)}...`
                          : item.ShipmentStatusDescription
                        }
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      <div className="d-flex gap-2">
                        <CTooltip content={t('updateShipmentStatus')}>
                          <div
                            className="cursor-pointer p-1"
                            onClick={() => handleEdit(item)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ebedef'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon icon={cilPencil} size="sm" style={{ color: mainColors.primary }} />
                          </div>
                        </CTooltip>
                        <CTooltip content={t('deleteShipmentStatus')}>
                          <div
                            className="cursor-pointer p-1"
                            onClick={() => handleDeleteClick(item)}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#ebedef'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                          >
                            <CIcon icon={cilTrash} size="sm" style={{ color: '#dc3545' }} />
                          </div>
                        </CTooltip>
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))
              ) : (
                <CTableRow>
                  <CTableDataCell colSpan={columns.length} className="text-center">
                    {t('No data available')}
                  </CTableDataCell>
                </CTableRow>
              )}
            </CTableBody>
          </CTable>
          
          {totalRows > 0 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">{t('Show')}:</span>
                <select 
                  className="form-select form-select-sm" 
                  value={rowsPerPage}
                  onChange={(e) => handleChangeRowsPerPage(parseInt(e.target.value, 10))}
                  style={{ width: 'auto' }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-muted">{t('entries')}</span>
              </div>
              
              <CPagination className="mt-3 justify-content-center" aria-label={t('Table navigation')}>
                <CPaginationItem 
                  aria-label={t('Previous')}
                  disabled={page === 1}
                  onClick={() => handleChangePage(page - 1)}
                >
                  <span aria-hidden="true">&laquo;</span>
                </CPaginationItem>
                {paginationItems}
                <CPaginationItem 
                  aria-label={t('Next')}
                  disabled={page === totalPages}
                  onClick={() => handleChangePage(page + 1)}
                >
                  <span aria-hidden="true">&raquo;</span>
                </CPaginationItem>
              </CPagination>
              
              <div className="text-muted">
                {t('showing')} {Math.min((page - 1) * rowsPerPage + 1, totalRows)} {t('to')}{' '}
                {Math.min(page * rowsPerPage, totalRows)} {t('of')} {totalRows} {t('entries')}
              </div>
            </div>
          )}
        </CCardBody>
      </CCard>

      <CModal visible={deleteModal.open} onClose={handleDeleteCancel}>
        <CModalHeader>
          <CModalTitle>{t('Confirm Delete')}</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <p>{t('confirmDeleteShipmentStatus')}</p>
          {deleteModal.item && (
            <div className="bg-light p-3 rounded">
              <strong>{t('Status Name')}:</strong> {deleteModal.item.ShipmentStatusName}<br/>
              <strong>{t('Description')}:</strong> {deleteModal.item.ShipmentStatusDescription}<br/>
              <strong>ID:</strong> {deleteModal.item.ShipmentStatusId}
            </div>
          )}
          <p className="text-danger mt-2 mb-0">
            <small>{t('This action cannot be undone.')}</small>
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleDeleteCancel}>
            {t('Cancel')}
          </CButton>
          <CButton color="danger" onClick={handleDeleteConfirm}>
            {t('Delete')}
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    shipmentStatusList: state.shipmentStatusReducer?.shipmentStatusList || [],
    shipmentStatusData: state.shipmentStatusReducer?.shipmentStatusData || null,
    shipmentHistorySortData: state.shipmentStatusReducer?.shipmentHistorySortData || {},
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateShipmentStatusRequest: (payload) => {
      dispatch(updateShipmentStatusRequestAction(payload));
    },
    deleteShipmentStatusRequest: (payload) => {
      dispatch(deleteShipmentStatusRequestAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShipmentStatusTable);